/**
 * Where cars drive: traffic lanes recovered from the road ribbons.
 *
 * Like the footpaths (see `core/character/footpaths.ts`), these are read back
 * out of the draped ribbon buffer rather than sent separately: each road is a
 * run of cross-sections, a left and a right kerb vertex apiece, in the order
 * OSM drew the way. Each vertex's `side` attribute says where across the road
 * it really is (a kerb pulled in to meet the other carriageway is less than
 * full width), so the true centreline is found by interpolating to side 0.5.
 *
 * Whether a road is one-way is not in the buffer, but its width says so:
 * `streetHalfWidth` narrows one-way carriageways and slip roads and leaves
 * every two-way road at its style's full width. A one-way road runs the way
 * OSM drew it.
 *
 * Motorways and trunk roads, primary, secondary and tertiary roads carry
 * traffic, and ordinary streets a little. Service roads, driveways and car
 * park aisles do not. Cars keep right.
 */

import { ROAD_STYLE, ROAD_STYLE_HALF_WIDTH } from '../sources/shortbread.js';
import type { RoadStyle } from '../sources/shortbread.js';

export interface Lane {
  /** x, y, z triples in the chunk's local frame, on the road surface, in the direction of travel. */
  points: Float32Array;
  /** Cumulative distance to each point, metres. */
  distances: Float32Array;
  length: number;
  /** Cruising speed, m/s. */
  speed: number;
  /** Cars per kilometre of this lane at an ordinary daytime hour. */
  density: number;
  style: RoadStyle;
  /** Lanes side by side the same way share a group; `rank` 0 is the rightmost, where cars keep. */
  group: number;
  rank: number;
  /** Index, in the same chunk's lanes, of the lane to its left and to its right going the same way, or -1. */
  left: number;
  right: number;
  /** Part of a roundabout's ring: traffic on it has right of way over traffic entering. */
  roundabout: boolean;
}

/** A lane is at most this wide, metres. */
const LANE_WIDTH = 3.5;
/** Lanes shorter than this are not worth driving. */
const MIN_LENGTH = 8;

interface ClassTraffic {
  /** km/h on a two-way road, a one-way carriageway, and a slip road. */
  kmh: [number, number, number];
  /** Cars per km per lane by day. */
  density: number;
}

/** What drives on each road style that carries traffic. */
const TRAFFIC: Partial<Record<number, ClassTraffic>> = {
  [ROAD_STYLE.motorway]: { kmh: [90, 110, 70], density: 9 },
  [ROAD_STYLE.arterial]: { kmh: [70, 60, 50], density: 6 },
  [ROAD_STYLE.collector]: { kmh: [50, 50, 40], density: 3 },
  [ROAD_STYLE.street]: { kmh: [40, 40, 30], density: 1.2 },
};

/** Whether a road style carries traffic. */
export function carriesTraffic(style: number): boolean {
  return TRAFFIC[style] !== undefined;
}

/**
 * How a road of `style` drawn at `halfWidth` is laid out: one-way or not, and
 * how many lanes it has each way.
 */
export function laneLayout(style: RoadStyle, halfWidth: number): { oneway: boolean; lanes: number; slip: boolean } {
  const full = ROAD_STYLE_HALF_WIDTH[style];
  const oneway = halfWidth < full - 0.05;
  if (oneway) {
    const lanes = Math.max(1, Math.min(3, Math.floor((halfWidth * 2) / (LANE_WIDTH + 1))));
    return { oneway, lanes, slip: lanes === 1 && style === ROAD_STYLE.motorway };
  }
  return { oneway, lanes: halfWidth >= 7 ? 2 : 1, slip: false };
}

function finish(raw: number[], style: RoadStyle, speed: number, density: number): Lane {
  const points = rounded(rounded(raw));
  const count = points.length / 3;
  const distances = new Float32Array(count);
  for (let i = 1; i < count; i++) {
    distances[i] = distances[i - 1] + Math.hypot(
      points[i * 3] - points[(i - 1) * 3],
      points[i * 3 + 2] - points[(i - 1) * 3 + 2],
    );
  }
  return {
    points: new Float32Array(points), distances, length: distances[count - 1] ?? 0, speed, density, style,
    group: -1, rank: 0, left: -1, right: -1, roundabout: false,
  };
}

/**
 * A line of x, y, z triples with its corners cut (Chaikin): each segment
 * gives way to two points a quarter and three quarters along it, so every
 * corner becomes two of a quarter its angle. Done twice, a lane sampled every
 * 10 m runs as a curve: a car at speed follows it without the small kink at
 * every point that it would otherwise make. The ends stay where they are, so
 * lanes still meet.
 */
function rounded(points: number[]): number[] {
  const n = points.length / 3;
  if (n < 3) return points;
  const out = [points[0], points[1], points[2]];
  for (let i = 0; i < n - 1; i++) {
    for (const f of [0.25, 0.75]) {
      if ((i === 0 && f === 0.25) || (i === n - 2 && f === 0.75)) continue;
      for (let k = 0; k < 3; k++) out.push(points[i * 3 + k] * (1 - f) + points[(i + 1) * 3 + k] * f);
    }
  }
  out.push(points[(n - 1) * 3], points[(n - 1) * 3 + 1], points[(n - 1) * 3 + 2]);
  return out;
}

/**
 * Offset a centreline sideways, metres, positive to the right of the direction
 * it runs. The scene's z runs south, so right of (dx, dz) is (-dz, dx).
 */
function offsetLine(centre: number[], offset: number): number[] {
  const n = centre.length / 3;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - 1), b = Math.min(n - 1, i + 1);
    const dx = centre[b * 3] - centre[a * 3], dz = centre[b * 3 + 2] - centre[a * 3 + 2];
    const len = Math.hypot(dx, dz) || 1;
    out.push(centre[i * 3] - (dz / len) * offset, centre[i * 3 + 1], centre[i * 3 + 2] + (dx / len) * offset);
  }
  return out;
}

function reversed(points: number[]): number[] {
  const out: number[] = [];
  for (let i = points.length - 3; i >= 0; i -= 3) out.push(points[i], points[i + 1], points[i + 2]);
  return out;
}

/** One road of a ribbon buffer, before it is made lanes. */
interface Road {
  style: RoadStyle;
  halfWidth: number;
  layout: { oneway: boolean; lanes: number; slip: boolean };
  /** x, y, z triples along its middle, in the order it was drawn. */
  centre: number[];
  /** Set when it is part of a roundabout's ring not mapped one-way: its centreline the way traffic goes round. */
  ring?: number[];
}

/** How near two road ends' middles are to be one point, metres. */
const JOIN_NEAR = 0.3;
/** Least straightness, as the cosine of the bend, of a join: 30°. */
const JOIN_COS = Math.cos(Math.PI / 6);

/**
 * Join the pieces of a road the map merely split: where exactly two ends of
 * the same kind and width meet and nothing else that carries traffic does,
 * running straight on, the two are one road. Split under a footbridge at Landvetter, Härrydavägen
 * had a piece 7 m long, too short for a lane of its own: the cars came to its
 * gap and turned round. A one-way road joins only end to start; a two-way
 * one either way round. Rings are left as they are.
 */
export function joinSplitWays(roads: Road[]): void {
  const cell = (x: number, z: number) => `${Math.round(x / JOIN_NEAR)},${Math.round(z / JOIN_NEAR)}`;
  for (let changed = true; changed;) {
    changed = false;
    const ends = new Map<string, Array<{ road: Road; start: boolean }>>();
    for (const road of roads) {
      if (road.ring !== undefined || road.centre.length < 6) continue;
      const c = road.centre, m = c.length - 3;
      for (const [start, x, z] of [[true, c[0], c[2]], [false, c[m], c[m + 2]]] as const) {
        const k = cell(x, z);
        const list = ends.get(k);
        if (list === undefined) ends.set(k, [{ road, start }]); else list.push({ road, start });
      }
    }
    for (const list of ends.values()) {
      if (list.length !== 2) continue;
      const [a, b] = list;
      if (a.road === b.road || a.road.style !== b.road.style || Math.abs(a.road.halfWidth - b.road.halfWidth) > 0.01) continue;
      if (a.road.layout.oneway !== b.road.layout.oneway) continue;
      // Ordered so the first runs into the second.
      const [first, second] = !a.start ? [a, b] : [b, a];
      if (first.road.layout.oneway && (first.start || !second.start)) continue;
      const head = first.start ? reversed(first.road.centre) : first.road.centre;
      const tail = second.start ? second.road.centre : reversed(second.road.centre);
      // Straight on, not round a corner: a corner is a turn, slowed for.
      const n = head.length;
      const ix = head[n - 3] - head[n - 6], iz = head[n - 1] - head[n - 4];
      const ox = tail[3] - tail[0], oz = tail[5] - tail[2];
      if (ix * ox + iz * oz < JOIN_COS * Math.hypot(ix, iz) * Math.hypot(ox, oz)) continue;
      first.road.centre = [...head, ...tail.slice(3)];
      roads.splice(roads.indexOf(second.road), 1);
      changed = true;
      break;
    }
  }
}

/**
 * Find the roundabouts mapped as ordinary two-way roads, which is common:
 * OSM's `junction=roundabout` makes a way one-way without saying so, and the
 * vector tiles carry neither. Driven both ways, the ring locks solid.
 *
 * A ring shows by its shape. Taken anticlockwise, every piece of it bends
 * left on a tight circle, and the pieces join end to start all the way round:
 * either one way that goes most of the way round by itself (200° or more,
 * further than any street corner), or a loop of shorter pieces, each bending
 * left all along, that closes back on itself. Closing is what tells a ring,
 * so a loop's pieces may be on a circle up to `LOOP_RADIUS`: an interchange's
 * ring over a motorway is often 50 or 60 m across or more, and driven both
 * ways it locks solid. Pieces mapped one-way join the loop too, the way they
 * are drawn. Each piece found gets `ring`.
 */
export function findUnmarkedRings(roads: Road[]): void {
  const most = (200 * Math.PI) / 180;
  // The pieces' own bends leave out the turn where they meet, so a whole
  // ring measures well short of 360°.
  const loop = (180 * Math.PI) / 180;
  const piece = (15 * Math.PI) / 180;
  const candidates: Array<{ road: Road; way: number[]; turn: number }> = [];
  for (const road of roads) {
    if (road.style === ROAD_STYLE.motorway) continue;
    const oneway = road.layout.oneway;
    const forward = roundaboutTurn(road.centre, LOOP_RADIUS);
    const backward = forward > 0 || oneway ? 0 : roundaboutTurn(reversed(road.centre), LOOP_RADIUS);
    const turn = Math.max(forward, backward);
    if (turn < piece) continue;
    const way = forward > 0 ? road.centre : reversed(road.centre);
    // Most of the way round by itself, on a roundabout's tight circle.
    if (!oneway && Math.max(roundaboutTurn(road.centre), roundaboutTurn(reversed(road.centre))) >= most) {
      road.ring = way;
      continue;
    }
    candidates.push({ road, way, turn });
  }
  // Follow each piece's end to the start of the next, round to where it began.
  const near = (a: number[], i: number, b: number[], j: number) =>
    Math.hypot(a[i] - b[j], a[i + 2] - b[j + 2]) < 2;
  for (const first of candidates) {
    if (first.road.ring !== undefined) continue;
    const chain = [first];
    let turned = first.turn;
    let at = first;
    for (let step = 0; step < 12; step++) {
      const end = at.way.length - 3;
      const next = candidates.find(c => c !== at && near(at.way, end, c.way, 0));
      if (next === undefined) break;
      if (next === first) {
        if (turned >= loop) for (const c of chain) c.road.ring = c.way;
        break;
      }
      if (chain.includes(next)) break;
      chain.push(next);
      turned += next.turn;
      at = next;
    }
  }
}

/** Every traffic lane in a draped ribbon buffer. */
export function extractLanes(positions: Float32Array, indices: Uint32Array, attributes: Float32Array): Lane[] {
  const lanes: Lane[] = [];
  const quads = Math.floor(indices.length / 6);
  const roads: Road[] = [];

  const flush = (first: number, last: number) => {
    const style = Math.round(attributes[first * 4 + 2]) as RoadStyle;
    if (TRAFFIC[style] === undefined) return;
    const halfWidth = attributes[first * 4 + 3];
    // The true centreline: where each cross-section's side reaches 0.5.
    const centre: number[] = [];
    for (let a = first; a <= last + 2; a += 2) {
      const l = a * 3, r = (a + 1) * 3;
      const sl = attributes[a * 4], sr = attributes[(a + 1) * 4];
      const t = sr - sl > 1e-4 ? (0.5 - sl) / (sr - sl) : 0.5;
      centre.push(
        positions[l] + (positions[r] - positions[l]) * t,
        positions[l + 1] + (positions[r + 1] - positions[l + 1]) * t,
        positions[l + 2] + (positions[r + 2] - positions[l + 2]) * t,
      );
    }
    roads.push({ style, halfWidth, layout: laneLayout(style, halfWidth), centre });
  };

  let runStart = -1;
  let previous = -1;
  for (let q = 0; q < quads; q++) {
    const a = indices[q * 6];
    if (runStart < 0) {
      runStart = a;
    } else if (a !== previous + 2) {
      flush(runStart, previous);
      runStart = a;
    }
    previous = a;
  }
  if (runStart >= 0) flush(runStart, previous);

  findUnmarkedRings(roads);
  joinSplitWays(roads);

  let groups = 0;
  for (const { style, halfWidth, layout, centre, ring } of roads) {
    const traffic = TRAFFIC[style]!;
    // A roundabout's ring mapped as an ordinary road, without its one-way
    // (see `findUnmarkedRings`): driven one way only, round to the left, in a
    // single lane down its middle.
    if (ring !== undefined) {
      const lane = finish(ring, style, traffic.kmh[1] / 3.6, traffic.density);
      lane.group = groups++;
      lane.roundabout = true;
      if (lane.length >= MIN_LENGTH) lanes.push(lane);
      continue;
    }

    const roundabout = layout.oneway && style !== ROAD_STYLE.motorway && isRoundabout(centre);
    const speed = traffic.kmh[layout.slip ? 2 : layout.oneway ? 1 : 0] / 3.6;
    const add = (points: number[], group: number, rank: number) => {
      const lane = finish(points, style, speed, traffic.density);
      lane.group = group;
      lane.rank = rank;
      lane.roundabout = roundabout;
      if (lane.length >= MIN_LENGTH) lanes.push(lane);
    };

    if (layout.oneway) {
      const width = Math.min(LANE_WIDTH, (halfWidth * 2) / layout.lanes);
      const group = groups++;
      for (let i = 0; i < layout.lanes; i++) add(offsetLine(centre, (i - (layout.lanes - 1) / 2) * width), group, layout.lanes - 1 - i);
      continue;
    }
    // Two-way: each direction keeps to its right of the centreline.
    const width = Math.min(LANE_WIDTH, halfWidth / layout.lanes);
    const back = reversed(centre);
    const forward = groups++, backward = groups++;
    for (let i = 0; i < layout.lanes; i++) {
      const offset = (i + 0.5) * width;
      add(offsetLine(centre, offset), forward, layout.lanes - 1 - i);
      add(offsetLine(back, offset), backward, layout.lanes - 1 - i);
    }
  }

  // Each lane's neighbours the same way, for changing lanes.
  const byGroup = new Map<number, Map<number, number>>();
  lanes.forEach((lane, index) => {
    let ranks = byGroup.get(lane.group);
    if (ranks === undefined) byGroup.set(lane.group, (ranks = new Map()));
    ranks.set(lane.rank, index);
  });
  for (const lane of lanes) {
    const ranks = byGroup.get(lane.group)!;
    lane.left = ranks.get(lane.rank + 1) ?? -1;
    lane.right = ranks.get(lane.rank - 1) ?? -1;
  }
  return lanes;
}

/**
 * Whether a one-way centreline (x, y, z triples) is a piece of a roundabout's
 * ring. The vector tiles do not carry `junction=roundabout`, but the shape
 * gives one away: it bends left all the way, on a tight circle, for a short
 * stretch. Keeping right, traffic goes round a roundabout anticlockwise.
 * A motorway's cloverleaf loop bends right, and an ordinary bend in a
 * one-way street is far wider or turns both ways.
 */
export function isRoundabout(centre: ArrayLike<number>): boolean {
  return roundaboutTurn(centre) > 0;
}

/** How far a centreline that is a piece of a roundabout turns, radians, or 0 if it is not one. */
function roundaboutTurn(centre: ArrayLike<number>, maxRadius = 45): number {
  const n = centre.length / 3;
  if (n < 3) return 0;
  let turned = 0, length = 0;
  let px = centre[3] - centre[0], pz = centre[5] - centre[2];
  length += Math.hypot(px, pz);
  for (let i = 2; i < n; i++) {
    const dx = centre[i * 3] - centre[(i - 1) * 3], dz = centre[i * 3 + 2] - centre[(i - 1) * 3 + 2];
    const len = Math.hypot(dx, dz);
    if (len < 1e-3) continue;
    // Scene z runs south, so a left turn has a negative cross product.
    const turn = Math.atan2(px * dz - pz * dx, px * dx + pz * dz);
    if (turn > 0.06) return 0;
    turned -= turn;
    length += len;
    px = dx; pz = dz;
  }
  if (length > 300 || turned < (15 * Math.PI) / 180) return 0;
  const radius = length / turned;
  return radius > 5 && radius < maxRadius && turned >= (25 * Math.PI) / 180 * Math.min(1, 45 / maxRadius) ? turned : 0;
}

/** Widest circle a loop of road pieces may be on to be taken for a ring, metres of radius. */
const LOOP_RADIUS = 100;

/** A point, a direction and a gradient a given distance along a lane. */
export interface LaneSample {
  x: number;
  y: number;
  z: number;
  /** Unit direction of travel in x, z. */
  dx: number;
  dz: number;
  /** Rise per metre travelled. */
  grade: number;
}

export function sampleLane(lane: Lane, distance: number, out: LaneSample): LaneSample {
  const d = Math.min(lane.length, Math.max(0, distance));
  const count = lane.distances.length;
  let lo = 0;
  let hi = count - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (lane.distances[mid] <= d) lo = mid;
    else hi = mid;
  }
  const span = lane.distances[hi] - lane.distances[lo];
  const t = span > 1e-6 ? (d - lane.distances[lo]) / span : 0;
  const p = lane.points;
  const ax = p[lo * 3], ay = p[lo * 3 + 1], az = p[lo * 3 + 2];
  const bx = p[hi * 3], by = p[hi * 3 + 1], bz = p[hi * 3 + 2];
  out.x = ax + (bx - ax) * t;
  out.y = ay + (by - ay) * t;
  out.z = az + (bz - az) * t;
  // The heading and grade blend from one segment into the next across each
  // corner, so a car turns through the bend instead of snapping round at
  // every point of the line: at a point it is halfway between the two, and
  // it is the segment's own at the segment's middle.
  segment(p, lo, segmentA);
  let dx = segmentA[0], dz = segmentA[1], grade = segmentA[2];
  const other = t < 0.5 ? lo - 1 : lo + 1;
  if (other >= 0 && other < count - 1) {
    segment(p, other, segmentB);
    const w = Math.abs(0.5 - t);
    dx = dx * (1 - w) + segmentB[0] * w;
    dz = dz * (1 - w) + segmentB[1] * w;
    grade = grade * (1 - w) + segmentB[2] * w;
  }
  const len = Math.hypot(dx, dz);
  out.dx = len > 1e-6 ? dx / len : 0;
  out.dz = len > 1e-6 ? dz / len : 1;
  out.grade = grade;
  return out;
}

const segmentA: [number, number, number] = [0, 0, 0];
const segmentB: [number, number, number] = [0, 0, 0];

/** The unit direction (x, z) and grade of segment `i` of a line of x, y, z triples. */
function segment(p: Float32Array, i: number, out: [number, number, number]): void {
  const dx = p[(i + 1) * 3] - p[i * 3], dy = p[(i + 1) * 3 + 1] - p[i * 3 + 1], dz = p[(i + 1) * 3 + 2] - p[i * 3 + 2];
  const len = Math.hypot(dx, dz);
  out[0] = len > 1e-6 ? dx / len : 0;
  out[1] = len > 1e-6 ? dz / len : 1;
  out[2] = len > 1e-6 ? dy / len : 0;
}

/** Where a point falls on a lane: the distance along it, how far off it, and the lane's direction there. */
export interface LaneProjection {
  distance: number;
  off: number;
  dx: number;
  dz: number;
}

/**
 * The nearest point on `lane` to (x, z) in its chunk's frame, projected onto
 * its segments rather than snapped to its points, so a car handed from one
 * lane to another lands exactly where it was, not up to half a segment off.
 * Only segments within `reach` count; undefined if none is.
 */
export function projectOnLane(lane: Lane, x: number, z: number, reach: number, out: LaneProjection): LaneProjection | undefined {
  const p = lane.points;
  let best = reach;
  let found = false;
  for (let i = 0; i < lane.distances.length - 1; i++) {
    const ax = p[i * 3], az = p[i * 3 + 2];
    const ex = p[(i + 1) * 3] - ax, ez = p[(i + 1) * 3 + 2] - az;
    const len2 = ex * ex + ez * ez;
    if (len2 < 1e-8) continue;
    const t = Math.max(0, Math.min(1, ((x - ax) * ex + (z - az) * ez) / len2));
    const d = Math.hypot(ax + ex * t - x, az + ez * t - z);
    if (d >= best) continue;
    best = d;
    found = true;
    const len = Math.sqrt(len2);
    out.distance = lane.distances[i] + t * len;
    out.off = d;
    out.dx = ex / len;
    out.dz = ez / len;
  }
  return found ? out : undefined;
}

/** How a vehicle is driven in traffic: how hard it pulls away and brakes, and the gap it keeps. */
export interface Driver {
  /** m/s². */
  maxAccel: number;
  comfortBrake: number;
  /** Bumper to bumper at a standstill, metres, and the time gap at speed, seconds. */
  minGap: number;
  headway: number;
}

export const CAR_DRIVER: Driver = { maxAccel: 1.6, comfortBrake: 2.2, minGap: 2.5, headway: 1.3 };
/** A laden truck: slow away, braking early and gently, and keeping its distance. */
export const TRUCK_DRIVER: Driver = { maxAccel: 0.7, comfortBrake: 1.6, minGap: 3.5, headway: 1.8 };

/**
 * Acceleration by the Intelligent Driver Model: toward `desired` speed on an
 * open road, and easing off to keep a safe gap behind whatever is `gap`
 * metres ahead (bumper to bumper) doing `leaderSpeed`. m/s².
 */
export function idmAcceleration(speed: number, desired: number, gap: number, leaderSpeed: number, driver: Driver = CAR_DRIVER): number {
  const { maxAccel, comfortBrake, minGap, headway } = driver;
  const free = 1 - (speed / Math.max(0.1, desired)) ** 4;
  if (!Number.isFinite(gap)) return maxAccel * free;
  const wanted = minGap + Math.max(0, speed * headway + (speed * (speed - leaderSpeed)) / (2 * Math.sqrt(maxAccel * comfortBrake)));
  return maxAccel * (free - (wanted / Math.max(0.1, gap)) ** 2);
}

/**
 * The fastest a car may be going `remaining` metres before a turn that bends
 * its way by `turn` (the dot product of the directions in and out) and still
 * slow to the turn's speed at a comfortable 2 m/s². Straight on is no limit.
 */
export function turnSpeedLimit(turn: number, remaining: number): number {
  if (turn > 0.95) return Infinity;
  const through = 4 + 9 * Math.max(0, turn);
  return Math.sqrt(through * through + 4 * Math.max(0, remaining - 2));
}

/**
 * Whether something at offset (rx, rz) from a junction, heading (dx, dz) at
 * `speed`, is coming through it soon enough that a car waiting to pull out
 * should let it pass: on a line through the junction, approaching or just
 * in it, and less than five seconds away.
 */
export function comingThrough(rx: number, rz: number, dx: number, dz: number, speed: number): boolean {
  // Distance still to go to the junction, along its heading.
  const along = -(rx * dx + rz * dz);
  if (along < -3 || along > 60) return false;
  const across = Math.abs(rx * dz - rz * dx);
  if (across > 7) return false;
  return along < 10 || along / Math.max(1, speed) < 5;
}

/**
 * The way a car drives from the end of one lane onto another: a cubic curve
 * leaving in the direction it was going and arriving in the new lane's, as a
 * driver steers through a turn. Positions are x, z in one frame, heights at
 * each end; `length` is its length along the curve, and `turn` how far it
 * turns, radians.
 */
export interface TurnCurve {
  x: [number, number, number, number];
  z: [number, number, number, number];
  y0: number;
  y1: number;
  length: number;
  turn: number;
  /** Distance along the curve at each of `TURN_STEPS + 1` even steps of its parameter. */
  table: Float32Array;
}

const TURN_STEPS = 16;

export function makeTurn(
  x0: number, z0: number, y0: number, dx0: number, dz0: number,
  x1: number, z1: number, y1: number, dx1: number, dz1: number,
): TurnCurve {
  // Handles about 0.4 of the way across: a quarter circle's, near enough.
  const chord = Math.hypot(x1 - x0, z1 - z0);
  const c = chord * 0.4;
  const x: TurnCurve['x'] = [x0, x0 + dx0 * c, x1 - dx1 * c, x1];
  const z: TurnCurve['z'] = [z0, z0 + dz0 * c, z1 - dz1 * c, z1];
  const table = new Float32Array(TURN_STEPS + 1);
  let px = x0, pz = z0;
  for (let i = 1; i <= TURN_STEPS; i++) {
    const t = i / TURN_STEPS;
    const qx = bezier(x, t), qz = bezier(z, t);
    table[i] = table[i - 1] + Math.hypot(qx - px, qz - pz);
    px = qx; pz = qz;
  }
  const turn = Math.acos(Math.max(-1, Math.min(1, dx0 * dx1 + dz0 * dz1)));
  return { x, z, y0, y1, length: table[TURN_STEPS], turn, table };
}

/** Where a car `s` metres along a turn is, and which way it faces. */
export function sampleTurn(curve: TurnCurve, s: number, out: LaneSample): LaneSample {
  const d = Math.max(0, Math.min(curve.length, s));
  let i = 1;
  while (i < TURN_STEPS && curve.table[i] < d) i++;
  const a = curve.table[i - 1], b = curve.table[i];
  const t = (i - 1 + (b > a ? (d - a) / (b - a) : 0)) / TURN_STEPS;
  out.x = bezier(curve.x, t);
  out.z = bezier(curve.z, t);
  out.y = curve.y0 + (curve.y1 - curve.y0) * (curve.length > 0 ? d / curve.length : 1);
  const tx = bezierSlope(curve.x, t), tz = bezierSlope(curve.z, t), len = Math.hypot(tx, tz);
  out.dx = len > 1e-9 ? tx / len : 0;
  out.dz = len > 1e-9 ? tz / len : 1;
  out.grade = curve.length > 0 ? (curve.y1 - curve.y0) / curve.length : 0;
  return out;
}

function bezier(p: readonly number[], t: number): number {
  const u = 1 - t;
  return u * u * u * p[0] + 3 * u * u * t * p[1] + 3 * u * t * t * p[2] + t * t * t * p[3];
}

function bezierSlope(p: readonly number[], t: number): number {
  const u = 1 - t;
  return 3 * u * u * (p[1] - p[0]) + 6 * u * t * (p[2] - p[1]) + 3 * t * t * (p[3] - p[2]);
}
